import { useMemo, useState } from "react";
import type { Entity } from "@/lib/api";

/**
 * SPCH-6's "who is this?" control: a text box that suggests the organization's people as
 * you type, and creates a new person from a name nobody has met.
 *
 * Shared by the recording's Speakers tab (UI-5) and simple mode's Needs-you card (UI-16),
 * because naming a voice has to mean the same thing on both. Two copies would be two
 * chances for one of them to stop offering "link the person who already exists" and start
 * quietly making second Sannes.
 */
export function NamePicker({
  heading,
  persons,
  busy,
  onPick,
  onCancel,
  autoFocus = true,
}: {
  /** "Who is Speaker 2?" — the question this box is answering. */
  heading: string;
  persons: Entity[];
  busy: boolean;
  onPick: (who: { personId: string } | { name: string }) => void;
  onCancel: () => void;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim();

  const suggestions = useMemo(() => {
    const needle = trimmed.toLowerCase();
    return persons
      .filter((person) => needle === "" || person.name.toLowerCase().includes(needle))
      .slice(0, 6);
  }, [persons, trimmed]);

  // An exact-name match means "link that person", not "make a second one with the same
  // name" — the typeahead's whole point.
  const exact = persons.find((person) => person.name.toLowerCase() === trimmed.toLowerCase());

  return (
    <form
      className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] p-[var(--space-3)]"
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed === "" || busy) return;
        onPick(exact ? { personId: exact.id } : { name: trimmed });
      }}
    >
      <label className="flex flex-col gap-1 text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)]">
        {heading}
        <input
          type="text"
          value={query}
          autoFocus={autoFocus}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Type a name…"
          className="rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-2.5 py-1.5 text-[length:var(--text-sm)] font-normal text-[var(--text-primary)] outline-none focus:border-[var(--vl-bridge)]"
        />
      </label>

      {suggestions.length > 0 && (
        <ul className="flex flex-col gap-0.5">
          {suggestions.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onPick({ personId: person.id })}
                className="flex w-full items-center rounded-[var(--radius-sm)] px-2 py-1.5 text-left text-[length:var(--text-sm)] text-[var(--text-primary)] transition hover:bg-[var(--surface-3)] disabled:opacity-60"
              >
                {person.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={trimmed === "" || busy}
          className="rounded-[var(--radius-md)] bg-[var(--vl-bridge)] px-3 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-inverse)] transition hover:brightness-110 disabled:opacity-50"
        >
          {exact ? `Link ${exact.name}` : trimmed === "" ? "Save" : `Create ${trimmed}`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-[var(--radius-md)] px-3 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
